// src/components/IntakeFormModal.jsx
//
// Path 4D-light intake form (27 May 2026).
//
// Opens before Stripe checkout. Captures:
//   - Required: matter_summary, perspective, 4 strategy bands, stage, jurisdiction
//   - Optional: file_number, run_instructions
//
// Field naming follows api.js convention (narrative, opp_resources,
// client_funding, risk_appetite, urgency, instructions). Maps to canonical
// schema in calderbank-create-checkout Lambda. See SLIDER_MAPPING_SPEC.md.
//
// Style: mirrors UploadModal - calderbank.css classes (modal-overlay,
// modal-header, field-input, persp-row, btn-primary, btn-ghost).
//
// Props:
//   matter         { matter_id, matter_name, ... }
//   token          Cognito id token (not used directly here - passed to parent on submit)
//   initialPerspective    'PLAINTIFF' | 'DEFENDANT'  (default 'PLAINTIFF')
//   onClose        () => void
//   onSubmit       (intakeData) => Promise<void>   parent invokes createCheckoutSession with this

import React, { useState } from 'react';

const STAGE_OPTIONS = [
  { value: 'pre_proceedings', label: 'Pre-proceedings' },
  { value: 'pre_trial',       label: 'Pre-trial / Discovery' },
  { value: 'trial_imminent',  label: 'Trial imminent' },
  { value: 'post_judgment',   label: 'Post-judgment' },
  { value: 'appeal',          label: 'Appeal' },
];

const JURISDICTION_OPTIONS = [
  { value: 'NSW', label: 'NSW' },
  { value: 'VIC', label: 'VIC' },
  { value: 'QLD', label: 'QLD' },
  { value: 'WA',  label: 'WA'  },
  { value: 'SA',  label: 'SA'  },
  { value: 'TAS', label: 'TAS' },
  { value: 'ACT', label: 'ACT' },
  { value: 'NT',  label: 'NT'  },
  { value: 'CTH', label: 'Federal (CTH)' },
];

const OPP_OPTIONS = [
  'Unknown',
  'Under-resourced',
  'Adequately funded',
  'Deep pockets',
];

const FUNDING_OPTIONS = [
  'Cannot fund to trial',
  'Unlikely',
  'Uncertain',
  'Likely',
  'Can fund to trial',
];

const APPETITE_OPTIONS = [
  'Wants to settle',
  'Prefers settlement',
  'Open to either',
  'Prefers to fight',
  'Wants to fight',
];

const URGENCY_OPTIONS = [
  'No urgency',
  'Normal',
  'Pressing',
  'Urgent',
];

const NARRATIVE_MIN_WORDS = 30;

export default function IntakeFormModal({
  matter,
  initialPerspective = 'PLAINTIFF',
  onClose,
  onSubmit,
}) {
  const [perspective,   setPerspective]   = useState(initialPerspective);
  const [narrative,     setNarrative]     = useState('');
  const [stage,         setStage]         = useState('pre_trial');
  const [jurisdiction,  setJurisdiction]  = useState('NSW');
  const [oppResources,  setOppResources]  = useState('Adequately funded');
  const [clientFunding, setClientFunding] = useState('Uncertain');
  const [riskAppetite,  setRiskAppetite]  = useState('Open to either');
  const [urgency,       setUrgency]       = useState('Normal');
  const [fileNumber,    setFileNumber]    = useState('');
  const [instructions,  setInstructions]  = useState('');

  const [submitting, setSubmitting] = useState(false);
  const [error,      setError]      = useState('');

  // ── Word count + validation ──────────────────────────────────────────
  const narrativeWordCount = narrative.trim().split(/\s+/).filter(Boolean).length;
  const narrativeOk        = narrativeWordCount >= NARRATIVE_MIN_WORDS;
  const canSubmit          = narrativeOk && !submitting;

  // ── Submit ───────────────────────────────────────────────────────────
  async function handleSubmit() {
    if (!canSubmit) return;
    setError('');
    setSubmitting(true);
    try {
      // Snake_case field names match api.js run_context contract.
      const payload = {
        perspective,
        narrative:      narrative.trim(),
        stage,
        jurisdiction,
        opp_resources:  oppResources,
        client_funding: clientFunding,
        risk_appetite:  riskAppetite,
        urgency,
        file_number:    fileNumber.trim() || null,
        instructions:   instructions.trim() || null,
      };
      await onSubmit(payload);
      // Parent closes the modal on success (typically by redirecting to
      // Stripe Checkout or showing the saved-card confirm modal).
    } catch (e) {
      console.error('Intake submit failed:', e);
      setError(e.message || 'Failed to start run. Please try again.');
      setSubmitting(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={submitting ? undefined : onClose}>
      <div
        className="modal"
        onClick={e => e.stopPropagation()}
        style={{ width: 680, maxWidth: '95vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column' }}
      >
        <div className="modal-header">
          <span>LitFit&trade; analysis &mdash; {matter.matter_name}</span>
          <button
            className="modal-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >&times;</button>
        </div>

        <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>

          {/* ── Section 1: Perspective ─────────────────────────────── */}
          <label className="field-label">Perspective</label>
          <div className="persp-row">
            <button
              type="button"
              className={`persp-btn ${perspective === 'PLAINTIFF' ? 'persp-active' : ''}`}
              onClick={() => setPerspective('PLAINTIFF')}
              disabled={submitting}
            >Plaintiff</button>
            <button
              type="button"
              className={`persp-btn ${perspective === 'DEFENDANT' ? 'persp-active' : ''}`}
              onClick={() => setPerspective('DEFENDANT')}
              disabled={submitting}
            >Defendant</button>
          </div>

          {/* ── Section 2: Matter summary (the anchor) ─────────────── */}
          <label className="field-label">
            Matter summary <span style={{ color: 'var(--amber)' }}>*</span>
          </label>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8, lineHeight: 1.5 }}>
            A factual narrative of the dispute &mdash; parties, key events, what each side contends, current
            posture. At least {NARRATIVE_MIN_WORDS} words. This is the analytical anchor; LitFit cross-examines it
            against the uploaded documents.
          </div>
          <textarea
            className="field-input"
            value={narrative}
            onChange={e => setNarrative(e.target.value)}
            placeholder="e.g. Commercial lease dispute. Landlord (our client, defendant) terminated the lease in March 2025 following six months of rental arrears. Tenant claims wrongful termination on the basis that the landlord accepted late payments throughout 2024 and is now estopped from strict enforcement..."
            rows={8}
            disabled={submitting}
            style={{ resize: 'vertical', minHeight: 140, marginBottom: 4 }}
          />
          <div style={{
            fontSize: 11,
            color: narrativeOk ? 'var(--green)' : 'var(--text-muted)',
            marginBottom: 16,
            textAlign: 'right',
          }}>
            {narrativeWordCount} words
            {!narrativeOk && narrative.length > 0 && (
              <span> &mdash; need at least {NARRATIVE_MIN_WORDS}</span>
            )}
          </div>

          {/* ── Section 3: Stage + jurisdiction (procedural) ───────── */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 4 }}>
            <div>
              <label className="field-label">Matter stage</label>
              <select
                className="field-input"
                value={stage}
                onChange={e => setStage(e.target.value)}
                disabled={submitting}
              >
                {STAGE_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Jurisdiction</label>
              <select
                className="field-input"
                value={jurisdiction}
                onChange={e => setJurisdiction(e.target.value)}
                disabled={submitting}
              >
                {JURISDICTION_OPTIONS.map(j => <option key={j.value} value={j.value}>{j.label}</option>)}
              </select>
            </div>
          </div>

          {/* ── Section 4: Strategy bands (4 dropdowns, 2x2 grid) ─── */}
          <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-muted)', letterSpacing: '0.03em', textTransform: 'uppercase', marginBottom: 8, marginTop: 8 }}>
            Strategy context
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <div>
              <label className="field-label">Opponent resources</label>
              <select
                className="field-input"
                value={oppResources}
                onChange={e => setOppResources(e.target.value)}
                disabled={submitting}
              >
                {OPP_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Client budget</label>
              <select
                className="field-input"
                value={clientFunding}
                onChange={e => setClientFunding(e.target.value)}
                disabled={submitting}
              >
                {FUNDING_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Client appetite</label>
              <select
                className="field-input"
                value={riskAppetite}
                onChange={e => setRiskAppetite(e.target.value)}
                disabled={submitting}
              >
                {APPETITE_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="field-label">Client timeline</label>
              <select
                className="field-input"
                value={urgency}
                onChange={e => setUrgency(e.target.value)}
                disabled={submitting}
              >
                {URGENCY_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
              </select>
            </div>
          </div>

          {/* ── Section 5: Optional fields (file number + instructions) ── */}
          <div style={{ marginTop: 8 }}>
            <label className="field-label">File number (optional)</label>
            <input
              type="text"
              className="field-input"
              value={fileNumber}
              onChange={e => setFileNumber(e.target.value)}
              placeholder="e.g. 2024/CM/0142"
              disabled={submitting}
              style={{ marginBottom: 16 }}
            />
          </div>

          <label className="field-label">Run instructions (optional)</label>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8, lineHeight: 1.5 }}>
            For this run only. Use SUPPRESS / ASSUME / SCOPE OUT / REMOVE directives to tune the analysis.
            Leave blank for the standard analysis.
          </div>
          <textarea
            className="field-input"
            value={instructions}
            onChange={e => setInstructions(e.target.value)}
            placeholder={'e.g. SUPPRESS quantum of loss — expert report not yet received.\nASSUME limitation period is clear — confirmed by senior counsel.'}
            rows={3}
            disabled={submitting}
            style={{ resize: 'vertical', minHeight: 60 }}
          />

          {/* ── Status / error ───────────────────────────────────── */}
          {submitting && (
            <div className="status-msg">Starting run&hellip;</div>
          )}
          {error && (
            <div className="status-err">{error}</div>
          )}
        </div>

        <div className="modal-footer">
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            disabled={submitting}
          >Cancel</button>
          <button
            type="button"
            className="btn-primary"
            onClick={handleSubmit}
            disabled={!canSubmit}
            title={!narrativeOk ? `Matter summary needs at least ${NARRATIVE_MIN_WORDS} words` : ''}
          >
            {submitting ? 'Starting&hellip;' : 'Continue to confirmation and payment ($50) \u2192'}
          </button>
        </div>
      </div>
    </div>
  );
}
